import { Employee } from '../types';
import { 
  generateLocalizedName,
  getRandomDepartment,
  getRandomJobTitle,
  getRandomLocation,
  getRandomSkills,
  getRandomPerformanceRating,
  getAllDepartments,
  getAllLocations,
  getAllJobLevels
} from '../utils/localizedMockData';

// Enterprise-scale employee data generator
export class EmployeeService {

  static generateMockEmployees(count: number = 100): Employee[] {
    return Array.from({ length: count }, (_, index) => {
      const empId = (index + 1).toString().padStart(6, '0');
      const { firstName, lastName, fullName } = generateLocalizedName();
      const department = getRandomDepartment();
      const location = getRandomLocation();
      const position = getRandomJobTitle();
      const skills = getRandomSkills(5);
      const tenure = Math.round((Math.random() * 15 + 0.5) * 10) / 10;
      const performanceScore = Math.round((2.5 + Math.random() * 2.5) * 10) / 10;
      const engagementScore = Math.round((5 + Math.random() * 5) * 10) / 10;
      
      return {
        id: `EMP${empId}`,
        name: fullName,
        position,
        department,
        location,
        tenure,
        roleLevel: this.getRoleLevel(position),
        performanceScore,
        engagementScore,
        attritionRisk: Math.round(Math.random() * 40),
        leadershipPotential: Math.round(Math.random() * 100),
        skills,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@cloudsonmars.com`,
        hireDate: this.generateHireDate(tenure),
        lastPromotion: Math.random() > 0.3 ? this.generatePromotionDate() : undefined,
        successionReadiness: Math.round(Math.random() * 100),
        manager: generateLocalizedName().fullName
      };
    });
  }

  private static getRoleLevel(position: string): string {
    if (position.includes('VP') || position.includes('Chief') || position.includes('C-Level')) {
      return 'Executive';
    }
    if (position.includes('Director') || position.includes('Principal')) {
      return 'Director';
    }
    if (position.includes('Senior') || position.includes('Lead')) {
      return 'Senior';
    }
    if (position.includes('Manager')) {
      return 'Manager';
    }
    return Math.random() > 0.5 ? 'Mid' : 'Junior';
  }

  private static generateHireDate(tenure: number): string {
    const now = new Date();
    const hireDate = new Date(now.getTime() - (tenure * 365.25 * 24 * 60 * 60 * 1000));
    return hireDate.toISOString().split('T')[0];
  }

  private static generatePromotionDate(): string {
    const now = new Date();
    const promotionDate = new Date(now.getTime() - (Math.random() * 2 * 365.25 * 24 * 60 * 60 * 1000));
    return promotionDate.toISOString().split('T')[0];
  }


  // Enterprise search simulation
  static async searchEmployees(
    query: string, 
    filters: { department?: string; location?: string; roleLevel?: string }, 
    page: number = 1, 
    pageSize: number = 12
  ): Promise<{ employees: Employee[]; total: number; pages: number }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, Math.random() * 300 + 100));

    // Generate a larger dataset for search
    const allEmployees = this.generateMockEmployees(1000);
    
    // Apply filters
    let filteredEmployees = allEmployees;
    
    if (query) {
      const searchTerm = query.toLowerCase();
      filteredEmployees = filteredEmployees.filter(emp => 
        emp.name.toLowerCase().includes(searchTerm) ||
        emp.position.toLowerCase().includes(searchTerm) ||
        emp.department.toLowerCase().includes(searchTerm) ||
        emp.skills.some(skill => skill.toLowerCase().includes(searchTerm))
      );
    }

    if (filters.department) {
      filteredEmployees = filteredEmployees.filter(emp => 
        emp.department.toLowerCase() === filters.department?.toLowerCase()
      );
    }

    if (filters.location) {
      filteredEmployees = filteredEmployees.filter(emp => 
        emp.location.toLowerCase() === filters.location?.toLowerCase()
      );
    }

    if (filters.roleLevel) {
      filteredEmployees = filteredEmployees.filter(emp => 
        emp.roleLevel.toLowerCase() === filters.roleLevel?.toLowerCase()
      );
    }

    const total = filteredEmployees.length;
    const pages = Math.ceil(total / pageSize);
    const startIndex = (page - 1) * pageSize;
    const employees = filteredEmployees.slice(startIndex, startIndex + pageSize);

    return { employees, total, pages };
  }

  // Featured employees for quick access
  static getFeaturedEmployees(): Employee[] {
    // Generate featured employees with localized data
    const featuredEmployees: Employee[] = [];
    const departments = getAllDepartments();
    const locations = getAllLocations();
    
    // Generate 6 featured employees with different profiles
    for (let i = 0; i < 6; i++) {
      const { firstName, lastName, fullName } = generateLocalizedName();
      const position = getRandomJobTitle();
      const department = departments[i % departments.length];
      const location = locations[i % locations.length];
      const skills = getRandomSkills(5);
      const tenure = 4 + Math.random() * 8; // 4-12 years tenure
      
      featuredEmployees.push({
        id: `EMP00000${i + 1}`,
        name: fullName,
        position,
        department,
        location,
        tenure: Math.round(tenure * 10) / 10,
        roleLevel: i === 5 ? 'Executive' : i >= 3 ? 'Senior' : 'Mid',
        performanceScore: Math.round((4.3 + Math.random() * 0.7) * 10) / 10,
        engagementScore: Math.round((8.5 + Math.random() * 1.5) * 10) / 10,
        attritionRisk: Math.round(Math.random() * 20),
        leadershipPotential: 75 + Math.round(Math.random() * 25),
        skills,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@cloudsonmars.com`,
        hireDate: this.generateHireDate(tenure),
        lastPromotion: this.generatePromotionDate(),
        successionReadiness: 70 + Math.round(Math.random() * 30),
        manager: generateLocalizedName().fullName
      });
    }
    
    return featuredEmployees;
  }
}